// Motor de gráficos compartido (Fase 1 del roadmap de formularios MSP — ver
// PLAN.md). Wrapper de recharts vestido con la paleta hce-* — ver tema.ts
// para los valores concretos y el porqué de cada elección. Genérico a
// propósito: no sabe qué representan sus series (temperatura hoy, percentil
// de crecimiento en Fase 2), solo dibuja puntos {x,y} con estilo.
import type { JSX, ReactNode } from 'react';
import { useMemo } from 'react';
import {
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { STROKE_DASHARRAY, TEMA_GRAFICO } from './tema';
import type { GraficoSerieTemporalProps, Serie } from './tipos';

type FilaCombinada = { x: number } & Record<string, number>;

/**
 * Une las series por valor de X compartido en un único array de filas —
 * necesario para que el Tooltip de recharts muestre las 3 series alineadas
 * en un mismo hover (con `data` independiente por `<Line>` el tooltip no
 * sincroniza bien entre series con timestamps distintos). Una serie sin
 * medición en un X dado simplemente no aporta esa clave a la fila — recharts
 * corta la línea ahí (`connectNulls={false}`), no interpola.
 */
function combinarSeries(series: readonly Serie[]): FilaCombinada[] {
  const porX = new Map<number, FilaCombinada>();
  for (const serie of series) {
    for (const punto of serie.puntos) {
      const fila = porX.get(punto.x) ?? ({ x: punto.x } as FilaCombinada);
      fila[serie.id] = punto.y;
      porX.set(punto.x, fila);
    }
  }
  return Array.from(porX.values()).sort((a, b) => a.x - b.x);
}

interface TooltipPayloadEntry {
  readonly dataKey?: string | number;
  readonly value?: number;
  readonly color?: string;
  readonly name?: string;
  readonly unit?: string;
}

function TooltipHce({
  active,
  payload,
  label,
  formatoTooltipX,
}: {
  active?: boolean;
  payload?: readonly TooltipPayloadEntry[];
  label?: number;
  formatoTooltipX: (x: number) => string;
}): ReactNode {
  if (!active || !payload || payload.length === 0 || label === undefined) return null;
  return (
    <div className="rounded-md border border-border bg-hce-paper p-2 text-xs shadow-sm">
      <p className="mb-1 font-medium text-hce-ink">{formatoTooltipX(label)}</p>
      <dl className="flex flex-col gap-0.5">
        {payload.map((entrada) => (
          <div key={String(entrada.dataKey)} className="flex items-center gap-2">
            <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: entrada.color }} />
            <dt className="text-hce-muted">{entrada.name}</dt>
            <dd className="font-mono text-hce-ink">
              {entrada.value}
              {entrada.unit ? ` ${entrada.unit}` : ''}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export function GraficoSerieTemporal({
  series,
  bandas,
  eventos,
  formatoEjeX,
  formatoTooltipX,
  etiquetaEjeY,
  alto = 260,
  aria,
}: GraficoSerieTemporalProps): JSX.Element {
  const datos = useMemo(() => combinarSeries(series), [series]);
  const formatoTooltip = formatoTooltipX ?? formatoEjeX;

  return (
    // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- SVG de datos (recharts), no una imagen: role="img" es el patrón WAI-ARIA estándar para gráficos, un <img> real no aplica.
    <div role="img" aria-label={aria.label} className="w-full">
      <ResponsiveContainer width="100%" height={alto}>
        <ComposedChart data={datos} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid stroke={TEMA_GRAFICO.rejilla} strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="x"
            type="number"
            domain={['dataMin', 'dataMax']}
            tickFormatter={formatoEjeX}
            tick={{ fill: TEMA_GRAFICO.textoMuted, fontFamily: TEMA_GRAFICO.fontFamily, fontSize: 12 }}
            axisLine={{ stroke: TEMA_GRAFICO.rejilla }}
            tickLine={false}
          />
          <YAxis
            tick={{ fill: TEMA_GRAFICO.textoMuted, fontFamily: TEMA_GRAFICO.fontFamilyMono, fontSize: 12 }}
            axisLine={{ stroke: TEMA_GRAFICO.rejilla }}
            tickLine={false}
            width={40}
            label={etiquetaEjeY ? { value: etiquetaEjeY, angle: -90, position: 'insideLeft', fill: TEMA_GRAFICO.textoMuted, fontFamily: TEMA_GRAFICO.fontFamily, fontSize: 12 } : undefined}
          />
          <Tooltip content={<TooltipHce formatoTooltipX={formatoTooltip} />} cursor={{ stroke: TEMA_GRAFICO.rejilla }} />
          {series.length > 1 && (
            <Legend
              iconType="line"
              wrapperStyle={{ fontFamily: TEMA_GRAFICO.fontFamily, fontSize: 12, color: TEMA_GRAFICO.textoMuted }}
            />
          )}
          {bandas?.map((banda) => (
            <ReferenceArea key={banda.id} y1={banda.y1} y2={banda.y2} fill={banda.color} fillOpacity={0.15} ifOverflow="extendDomain" />
          ))}
          {eventos?.map((evento) => (
            <ReferenceLine
              key={`${evento.x}-${evento.etiqueta}`}
              x={evento.x}
              stroke={evento.color ?? TEMA_GRAFICO.textoMuted}
              strokeDasharray="4 3"
              label={{ value: evento.etiqueta, position: 'top', fill: TEMA_GRAFICO.textoMuted, fontFamily: TEMA_GRAFICO.fontFamily, fontSize: 11 }}
            />
          ))}
          {series.map((serie) => (
            <Line
              key={serie.id}
              dataKey={serie.id}
              name={serie.etiqueta}
              unit={serie.unidad}
              type="monotone"
              stroke={serie.color}
              strokeWidth={2}
              strokeDasharray={STROKE_DASHARRAY[serie.trazo ?? 'solido']}
              dot={{ r: 3, fill: serie.color, strokeWidth: 0 }}
              activeDot={{ r: 5 }}
              isAnimationActive={false}
              connectNulls={false}
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
